import { Button } from "@/components/ui/button";
import { CatalogWorkflowButtons } from "@/components/catalog/CatalogWorkflowButtons";
import { CatalogMobileActionBar } from "@/components/catalog/CatalogMobileActionBar";
import { JournalHoldingsEditor } from "@/components/catalog/JournalHoldingsEditor";
import { JournalMetadataFields } from "@/components/catalog/JournalMetadataFields";
import type { CatalogWorkflowAction, PublicationStatus } from "@/lib/catalog/catalog-workflow";
import type { JournalCatalogOptions } from "@/pages/admin/catalog/useJournalCatalogOptions";
import type { JournalCatalogState } from "@/pages/admin/catalog/journal-catalog-form";

interface JournalCatalogFormProps {
  state: JournalCatalogState;
  onChange: (state: JournalCatalogState) => void;
  errors: Record<string, string>;
  options: JournalCatalogOptions;
  submitting: boolean;
  publicationStatus?: PublicationStatus;
  onAction: (action: CatalogWorkflowAction) => void;
  onCancel: () => void;
}

const INLINE_ERROR_FIELDS = ["title", "editors", "subjects", "volumeNumber", "issn", "websiteUrl"];

function isInlineHoldingError(key: string) {
  return /^journalHoldings\.\d+\.volumeNumber$/.test(key);
}

export function JournalCatalogForm({
  state,
  onChange,
  errors,
  options,
  submitting,
  publicationStatus,
  onAction,
  onCancel,
}: JournalCatalogFormProps) {
  const workflowButtons = (
    <CatalogWorkflowButtons
      catalogSettings={options.settings}
      loading={submitting}
      publicationStatus={publicationStatus}
      createLabel="Publish"
      onAction={onAction}
      layout="inline"
    />
  );
  const summaryErrors = Object.entries(errors).filter(
    ([key]) => !INLINE_ERROR_FIELDS.includes(key) && !isInlineHoldingError(key)
  );
  return (
    <>
      <JournalMetadataFields
        value={state.biblio}
        onChange={(biblio) => onChange({ ...state, biblio })}
        errors={errors}
      />
      <JournalHoldingsEditor
        holdings={state.holdings}
        onChange={(holdings) => onChange({ ...state, holdings })}
        itemDefaults={state.itemDefaults}
        onItemDefaultsChange={(itemDefaults) => onChange({ ...state, itemDefaults })}
        errors={errors}
        options={options}
      />
      {summaryErrors.length > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {summaryErrors.map(([key, message]) => (
            <p key={key}>{message}</p>
          ))}
        </div>
      ) : null}
      <div className="hidden items-center justify-between gap-4 lg:flex">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {workflowButtons}
      </div>
      <CatalogMobileActionBar>
        <div className="flex w-full flex-col gap-2">{workflowButtons}</div>
      </CatalogMobileActionBar>
    </>
  );
}
